import React from 'react';
import { GenericReducer, GenericReducerFunctionMap } from 'contexts/GenericReducer';

import axios from 'axios';
import config from 'config';

import Loading from 'components/Loading';

// #region Contexts

const contentContext = React.createContext();
function useConfigContext() {
  const context = React.useContext(contentContext);
  if (context === undefined) {
    throw new Error('useConfigContext must be used within an ConfigProvider');
  }
  return context;
}

const dispatchContext = React.createContext();
function useConfigDispatchContext() {
  const context = React.useContext(dispatchContext);
  if (context === undefined) {
    throw new Error('ConfigDispatchContext must be used within an ConfigProvider');
  }
  return context;
}
// #endregion

// #region Helper Functions

function getDefaultEndpoint(context, dispatch) {
  // if we are running in a dev environment, use the config endoint
  if (window.location.hostname.includes('localhost') || window.location.hostname.startsWith('192.168.')) {
    // console.log("Running in development mode, targeting dev endpoint")
    return config.endpoint;
  }

  // If I am on one of the direct pages use the page config.
  const hostendpoint = window.location.host;
  return hostendpoint;
}

function getEndpoint(context, dispatch) {
  return context.endpoint || getDefaultEndpoint(context, dispatch);
}

function setEndpoint(context, dispatch, endpoint) {
  dispatch({ type: 'SET_ENDPOINT', payload: endpoint });
}

function getApiEndpoint(context, dispatch) {
  return `https://${getEndpoint(context, dispatch)}/api`;
}

function getAuthRedirectEndpoint(context, dispatch) {
  // return `https://${getEndpoint(context, dispatch)}/oauth2/idpresponse`;
  // I need to returh https://localhost:3000 if I am in dev mode but the regular endpoint if I am not
  if (window.location.hostname.includes('localhost') || window.location.hostname.startsWith('192.168.')) {
    return `http://localhost:3000`;
  }
  return `https://${getEndpoint(context, dispatch)}`;
}

function getAuthConfig(context, dispatch) {
  // console.log("getAuthConfig")
  // console.log("current_config", context)
  try {
    const return_config = {
      region: context.aws_region,
      userPoolId: context.user_pool_id,
      userPoolWebClientId: context.cognito_user_pool_client_id,
      oauth: {
        domain: context.cognito_custom_domain,
        scope: ['email', 'openid', 'profile'],
        // redirectSignIn: 'http://localhost:3000/oauth2/idpresponse', // or your production URL
        // redirectSignOut: 'http://localhost:3000/',
        redirectSignIn: getAuthRedirectEndpoint(context, dispatch) + '/oauth2/idpresponse',
        redirectSignOut: getAuthRedirectEndpoint(context, dispatch) + '/',
        responseType: 'code', // 'code' for Authorization code grant
      },
    }
    // console.log("what I would return", return_config)
    return return_config;
  } catch (error) {
    console.error('Insufficent public config data to create auth config', error)
    throw new Error("Insufficent public config data to create auth config");
  }

}

function isProgrammaticOnly(context, dispatch) {
  //  first get the endpoint and check if it is targeting one of the sites for which the frontend isn't supported
  const endpoint = getEndpoint(context, dispatch);
  if (endpoint.includes("p")) {
    // next check if there is a local value override set
    const localValue = window.localStorage.getItem('frontend_allowed_override');
    if (localValue === 'true') {
      return false;
    } else {
      return true
    }
  }
  return false;
}




export function postAPI(context, dispatch, path, body, requestConfig) {
  // Determine proper endpoint
  const endpoint = getApiEndpoint(context, dispatch);

  // Build call structure
  if (!requestConfig) {
    requestConfig = {};
  }
  requestConfig.headers = {
    accept: 'application/json',
  };

  return axios.post(endpoint + path, body, requestConfig);
}

function LogConfigSettings(context) {
  console.log("tada")
  console.log('context', context);
}

function getPublicSiteConfig(context, dispatch) {
  return axios.get(`https://${getEndpoint(context, dispatch)}/public_config.json`)
    .then((response) => {
      dispatch({ type: 'SET_CONFIG', payload: response.data });
      return response.data;
    })
    .catch((error) => {
      console.error('Error getting public site config', error);
    });
}

export function useConfigFunctions() {
  const context = useConfigContext();
  const dispatch = useConfigDispatchContext();
  const authFunctions = {
    logSettings: () => LogConfigSettings(context, dispatch),
    getEndpoint: () => getEndpoint(context, dispatch),
    postAPI: (path, body, requestConfig) => postAPI(context, dispatch, path, body, requestConfig),
    getAuthConfig: () => getAuthConfig(context, dispatch),
    getApiEndpoint: () => getApiEndpoint(context, dispatch),
  };
  return authFunctions;
}

export function useConfigState() {
  const context = useConfigContext();
  return context;
}

export function ConfigProvider({ children, ...rest }) {
  const [configuration, setConfiguration] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  const [programmaticOnly, setProgrammaticOnly] = React.useState(true);

  const [configContextState, configContextDispatch] = React.useReducer(GenericReducer, {
    ...configuration,
  });

  React.useEffect(() => {
    setEndpoint(configContextState, configContextDispatch, getDefaultEndpoint(configContextState, configContextDispatch));
    const programmatic = isProgrammaticOnly(configContextState, configContextDispatch);
    if (!programmatic) {
      setProgrammaticOnly(programmatic);
    } else {
      setLoaded(true);
    }

  }, []);

  React.useEffect(() => {
    setEndpoint(configContextState, configContextDispatch, getDefaultEndpoint(configContextState, configContextDispatch));
    getPublicSiteConfig(configContextState, configContextDispatch)
      .then((result) => {
        GenericReducerFunctionMap(configContextDispatch).UpdateContextStateMap(result)
        setLoaded(true);
      })
      .catch((e) => {
        console.error('Error loading public site config', e);
        setLoaded(true);
      })
  }, [programmaticOnly]);

  return programmaticOnly ? (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100vw',
      height: '100vh',
      backgroundColor: '#f0f0f0', // Optional: Set a background color
    }}>
      <h2>Programmatic Access Only</h2>
    </div>
  ) :

    (loaded ?
      (
        <contentContext.Provider value={configContextState}>
          <dispatchContext.Provider value={configContextDispatch}>
            {children}
          </dispatchContext.Provider>
        </contentContext.Provider>
      ) : (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100vw',
          height: '100vh',
          backgroundColor: '#f0f0f0', // Optional: Set a background color
        }}>
          <Loading message="Configuring..." />
        </div>
      )
    )
}
